import { IconButton } from "@mui/material";
import { isEqual } from "lodash";
import { ReactElement, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { PIC } from "@ryde/assets";
import {
  PageHeaderContext,
  useComposedSearchCriteria,
  useFiltersActiveState,
} from "@ryde/redux";
import {
  IExtendableDto,
  isEntityServerSideFiltered,
  ITypeormWhereCriteria,
  lastSearchCriteriaAtomFamily,
  pageEntityAtom,
  RideEntity,
} from "@ryde/shared";

export const FiltersApply = (): ReactElement => {
  const entity = useRecoilValue(pageEntityAtom);
  const { onFiltersApply } = useContext(PageHeaderContext);
  const { isAnyFilterActive } = useFiltersActiveState();
  const { getDynamicFormsComposedCriteria } = useComposedSearchCriteria(entity);

  const lastSearchCriteria = useRecoilValue(
    lastSearchCriteriaAtomFamily(entity),
  );
  const [currentComposedQueryObject, setCurrentComposedQueryObject] = useState<
    ITypeormWhereCriteria<IExtendableDto | RideEntity>
  >({});
  const { t } = useTranslation();

  const onClick = useEvent((): void => {
    onFiltersApply?.();
  });

  const filterCanBeApplied = useMemo(() => {
    if (isEntityServerSideFiltered(entity)) {
      return !isEqual(currentComposedQueryObject, lastSearchCriteria);
    }

    return isAnyFilterActive;
  }, [
    currentComposedQueryObject,
    entity,
    isAnyFilterActive,
    lastSearchCriteria,
  ]);

  useEffect(() => {
    setCurrentComposedQueryObject(getDynamicFormsComposedCriteria());
  }, [getDynamicFormsComposedCriteria]);

  return (
    <IconButton
      onClick={onClick}
      title={t(`general.APPLY_FILTERS`)}
      color={filterCanBeApplied ? "primary" : "default"}
    >
      <PIC.FilterApplySvg />
    </IconButton>
  );
};
